import io from "socket.io-client";
import { store } from "../../Redux/store";
import { handleNotification } from "../../handlers/notificationHandler";

// --- Configuration ---
const SOCKET_PATH = "/v1/api/notify/socket.io/";
const SOCKET_RETRY_DELAY = 120000; // 2 minutes
const ENABLE_SOCKET_RETRY = false; // Toggle to enable/disable automatic socket retry

/**
 * Get the base URL based on environment
 * @returns {string} Base URL for the API
 */
const getBaseUrl = () => {
  if (process.env.REACT_APP_ENV === "qa")
    return "https://api-otc-qa-v2.gna.energy";
  if (process.env.REACT_APP_ENV === "prod")
    return "https://prod-otc-v2-api.gna.energy";
  return "https://stage-otc-v2-api.gna.energy";
};

// --- State (Module Scope) ---
let globalSocket = null;
let longPollAbortController = null;
let isConnected = false;
let socketRetryTimeout = null;

// Connection Strategy State
let connectionMode = "socket"; // 'socket' | 'long-poll'
let socketFailCount = 0;
const MAX_SOCKET_FAILS = 3;

// Observer Pattern for React Components
let subscribers = [];
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 5;

// --- Helpers ---
/**
 * Get authentication token from localStorage
 * @returns {string|null} Authentication token
 */
const getToken = () => localStorage.getItem("OTC_V2_TOKEN");

/**
 * Notify all subscribers with notification data
 * @param {Object} data - Notification data
 */
const notifySubscribers = (data) => {
  if (data?.hasUpdates) {
    for (let i = 0; i < data?.notifications?.length; i++) {
      try {
        handleNotification(store.dispatch, data?.notifications[i]);
      } catch (error) {
        console.error("Error notifying subscriber:", error);
      }
    }
  }
};

// --- Long Polling Logic ---
/**
 * Stop long polling and cleanup abort controller
 */
const stopLongPolling = () => {
  if (longPollAbortController) {
    longPollAbortController.abort();
    longPollAbortController = null;
    console.debug("🛑 Long polling stopped");
  }
};

/**
 * Get user ID from Redux store
 * @returns {string|null} User ID
 */
const getUserId = () => {
  const state = store.getState();
  return state?.persistedReducer?.user?.user?.userId || null;
};

/**
 * Long polling implementation for fallback connectivity
 */
const longPoll = async () => {
  if (connectionMode !== "long-poll") return;

  try {
    const token = getToken();
    const userId = getUserId();
    if (!token) {
      console.warn("No token available for long polling");
      return;
    }
    if (!userId) {
      console.warn("No user ID available for long polling");
      return;
    }
    longPollAbortController = new AbortController();

    const response = await fetch(
      `${getBaseUrl()}/v1/api/notify/notifications/poll?userId=${userId}&timeout=50`,
      {
        method: "GET",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        signal: longPollAbortController.signal,
      },
    );

    if (response.ok) {
      const data = await response.json();
      if (data && Object.keys(data).length > 0) {
        console.log("📊 Long poll notification:", data);
        notifySubscribers(data);
        isConnected = true;
        reconnectAttempts = 0;
      }
    } else if (response.status === 401) {
      console.error("Authentication failed for long polling");
      disconnect();
      return;
    } else {
      console.warn(`Long poll received status: ${response.status}`);
    }

    // Continue long polling if still in long-poll mode
    if (connectionMode === "long-poll") {
      setTimeout(() => longPoll(), 1000); // Small delay before next request
    }
  } catch (error) {
    if (error.name === "AbortError") {
      console.debug("Long poll aborted");
      return;
    }

    console.error("Long polling error:", error);
    reconnectAttempts++;

    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.error(
        "Max reconnect attempts reached for long polling. Service unavailable.",
      );
      isConnected = false;
      return;
    }

    // Exponential backoff for retries
    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
    if (connectionMode === "long-poll") {
      setTimeout(() => longPoll(), delay);
    }
  }
};

/**
 * Switch to long polling mode and start polling
 */
const startLongPolling = () => {
  stopLongPolling();

  connectionMode = "long-poll";
  console.log("🔄 Switching to Long Polling Service...");
  reconnectAttempts = 0;
  isConnected = true;
  longPoll();

  // Schedule retry to socket after delay (if enabled)
  if (ENABLE_SOCKET_RETRY) {
    scheduleSocketRetry();
  }
};

// --- Socket Logic ---
/**
 * Schedule a retry to switch back to socket mode
 */
const scheduleSocketRetry = () => {
  // Clear existing retry timeout
  if (socketRetryTimeout) {
    clearTimeout(socketRetryTimeout);
    socketRetryTimeout = null;
  }

  console.log(
    `⏱️ Scheduling socket retry in ${SOCKET_RETRY_DELAY / 1000} seconds...`,
  );

  socketRetryTimeout = setTimeout(() => {
    if (connectionMode === "long-poll") {
      console.log("🔄 Attempting to switch back to socket mode...");
      socketFailCount = 0; // Reset fail count
      connectionMode = "socket";
      stopLongPolling();
      connect();
    }
  }, SOCKET_RETRY_DELAY);
};

/**
 * Cleanup socket connection and remove all listeners
 */
const cleanupSocket = () => {
  if (globalSocket) {
    globalSocket.removeAllListeners();
    globalSocket.disconnect();
    globalSocket = null;
  }
};

/**
 * Establish socket connection with fallback to long polling
 * @returns {Object|null} Socket instance or null
 */
const connect = () => {
  if (globalSocket && isConnected && connectionMode === "socket") {
    return globalSocket;
  }

  // If in long-poll mode, start long polling
  if (connectionMode === "long-poll") {
    startLongPolling();
    return null;
  }

  const token = getToken();
  if (!token) {
    console.warn("No token available, cannot connect");
    return null;
  }

  cleanupSocket();

  globalSocket = io(getBaseUrl(), {
    path: SOCKET_PATH,
    auth: { token },
    forceNew: true,
    timeout: 20000,
    reconnection: true,
    reconnectionAttempts: MAX_SOCKET_FAILS,
    reconnectionDelay: 2000,
    reconnectionDelayMax: 10000,
    transports: ["websocket", "polling"],
  });

  // Connection Success
  globalSocket.on("connect", () => {
    isConnected = true;
    socketFailCount = 0;
    reconnectAttempts = 0;
    connectionMode = "socket";
    console.debug("🔌 Socket connected successfully");

    // Stop any fallback polling and clear retry timeout
    stopLongPolling();
    if (socketRetryTimeout) {
      clearTimeout(socketRetryTimeout);
      socketRetryTimeout = null;
    }
  });

  // Disconnection
  globalSocket.on("disconnect", (reason) => {
    isConnected = false;
    console.warn("⚠️ Socket disconnected:", reason);

    // If disconnect was intentional, don't fallback
    if (
      reason === "io client disconnect" ||
      reason === "io server disconnect"
    ) {
      return;
    }
  });

  // Connection Errors
  globalSocket.on("connect_error", (error) => {
    console.error("❌ Socket connection error:", error.message);
    socketFailCount++;

    if (socketFailCount >= MAX_SOCKET_FAILS) {
      console.warn(
        `🚨 Socket failed ${socketFailCount} times. Falling back to Long Polling.`,
      );

      cleanupSocket();
      startLongPolling();
    }
  });

  // Reconnection Attempts
  globalSocket.on("reconnect_attempt", (attempt) => {
    console.debug("♻️ Socket reconnection attempt:", attempt);

    // Refresh token on reconnect
    const freshToken = getToken();
    if (globalSocket && freshToken) {
      globalSocket.auth = { token: freshToken };
    }
  });

  // Failed to reconnect after all attempts
  globalSocket.on("reconnect_failed", () => {
    console.error("❌ Socket reconnection failed after all attempts");
    cleanupSocket();
    startLongPolling();
  });

  // Handle Incoming Notifications
  globalSocket.on("portal-notification", (data) => {
    console.log("📢 Socket notification:", data);
    notifySubscribers(data);
  });

  // Handle Errors
  globalSocket.on("error", (error) => {
    console.error("❌ Socket error:", error);
  });

  return globalSocket;
};

/**
 * Get the current socket instance
 * @returns {Object|null} Socket instance or null
 */
const getSocket = () => {
  if (!globalSocket && connectionMode === "socket") {
    return connect();
  }
  return globalSocket;
};

/**
 * Disconnect and cleanup all connections
 */
const disconnect = () => {
  console.log("🔌 Disconnecting service...");

  cleanupSocket();
  stopLongPolling();

  if (socketRetryTimeout) {
    clearTimeout(socketRetryTimeout);
    socketRetryTimeout = null;
  }

  isConnected = false;
  socketFailCount = 0;
  reconnectAttempts = 0;
  connectionMode = "socket";
  subscribers = [];
};

// --- Subscription API ---
/**
 * Subscribe to notifications
 * @param {Function} callback - Callback function to handle notifications
 * @returns {Function} Unsubscribe function
 */
const subscribe = (callback) => {
  if (typeof callback !== "function") {
    console.error("Subscribe requires a callback function");
    return () => {};
  }

  subscribers.push(callback);

  // Auto-connect if not connected
  if (!isConnected && !globalSocket && !longPollAbortController) {
    connect();
  }

  // Return unsubscribe function
  return () => {
    subscribers = subscribers.filter((cb) => cb !== callback);

    // Auto-disconnect if no more subscribers
    if (subscribers.length === 0) {
      disconnect();
    }
  };
};

// --- Status API ---
/**
 * Get current connection status
 * @returns {Object} Status object with connection details
 */
const getStatus = () => ({
  isConnected,
  connectionMode,
  socketFailCount,
  subscriberCount: subscribers.length,
  reconnectAttempts,
});

export default {
  connect,
  getSocket,
  disconnect,
  subscribe,
  getStatus,
};
